import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Trash2, CalendarOff, Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminAbsencesSection = () => {
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ operator_name: '', date_from: '', date_to: '', reason: '' });
  const { getAuthHeaders } = useAuth();

  useEffect(() => { fetchAbsences(); }, []);

  const fetchAbsences = async () => {
    try {
      const response = await axios.get(`${API}/absences`, { headers: getAuthHeaders() });
      setAbsences(response.data);
    } catch (error) {
      // API might not exist yet
      setAbsences([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/absences`, formData, { headers: getAuthHeaders() });
      toast.success('Assenza registrata!');
      setDialogOpen(false);
      setFormData({ operator_name: '', date_from: '', date_to: '', reason: '' });
      fetchAbsences();
    } catch (error) {
      toast.error('Errore nella registrazione');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/absences/${id}`, { headers: getAuthHeaders() });
      toast.success('Assenza rimossa');
      fetchAbsences();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-48"><Loader2 className="h-8 w-8 animate-spin text-accent" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="absences-section">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold mb-2">Assenze Operatori</h2>
          <p className="text-muted-foreground text-sm">Gestisci le assenze e i permessi</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="add-absence-btn">
              <Plus className="h-4 w-4 mr-2" /> Nuova Assenza
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Registra Assenza</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Operatore *</Label>
                <Input required value={formData.operator_name} onChange={(e) => setFormData({ ...formData, operator_name: e.target.value })} placeholder="Nome operatore" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Data Inizio *</Label>
                  <Input type="date" required value={formData.date_from} onChange={(e) => setFormData({ ...formData, date_from: e.target.value })} />
                </div>
                <div>
                  <Label>Data Fine *</Label>
                  <Input type="date" required value={formData.date_to} onChange={(e) => setFormData({ ...formData, date_to: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Motivo</Label>
                <Input value={formData.reason} onChange={(e) => setFormData({ ...formData, reason: e.target.value })} placeholder="Motivo assenza" />
              </div>
              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">Annulla</Button>
                <Button type="submit" className="flex-1 bg-accent text-accent-foreground hover:bg-accent/90">Registra</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {absences.length === 0 ? (
        <div className="text-center py-12">
          <CalendarOff className="h-12 w-12 text-muted-foreground mx-auto mb-3 opacity-50" />
          <p className="text-muted-foreground">Nessuna assenza registrata</p>
        </div>
      ) : (
        <div className="space-y-3">
          {absences.map((absence) => (
            <Card key={absence.id} className="card-elegant" data-testid={`absence-${absence.id}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold">{absence.operator_name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {absence.date_from} - {absence.date_to}
                    {absence.reason && ` | ${absence.reason}`}
                  </p>
                </div>
                <Button variant="destructive" size="sm" onClick={() => handleDelete(absence.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminAbsencesSection;
